/** @format */

import { useEffect } from "react";

interface SEOProps {
	title?: string;
	description?: string;
	keywords?: string;
	image?: string;
	url?: string;
	type?: string;
}

/**
 * SEO Component - Updates document meta tags for better search engine optimization
 *
 * Usage:
 * <SEO
 *   title="Page Title"
 *   description="Page description for search results"
 *   keywords="keyword1, keyword2, keyword3"
 * />
 */
const SEO: React.FC<SEOProps> = ({
	title,
	description,
	keywords,
	image = "https://imagefile-bucket.s3.us-east-2.amazonaws.com/primary-logo-1-1.png",
	url,
	type = "website",
}) => {
	const siteName = "FundsyFi";
	const defaultTitle =
		"FundsyFi - Community Savings Circles & Circle Contributions Platform";
	const defaultDescription =
		"Join FundsyFi to participate in trusted savings circles, group contributions, and community based financial planning. Build wealth together with secure, transparent, and community driven savings.";
	const defaultKeywords =
		"savings circles, group contributions, community savings, rotating savings, ROSCA, susu, tontine, chit fund, money pool, group savings, financial planning, community finance";
	const baseUrl = "https://www.fundsyfi.com";

	const fullTitle = title ? `${title} | ${siteName}` : defaultTitle;
	const fullDescription = description || defaultDescription;
	const fullKeywords = keywords || defaultKeywords;
	const fullUrl = url ? `${baseUrl}${url}` : baseUrl;

	useEffect(() => {
		// Update document title
		document.title = fullTitle;

		// Helper function to update or create meta tags
		const updateMetaTag = (
			name: string,
			content: string,
			property?: boolean,
		) => {
			const attribute = property ? "property" : "name";
			let element = document.querySelector(`meta[${attribute}="${name}"]`);

			if (element) {
				element.setAttribute("content", content);
			} else {
				element = document.createElement("meta");
				element.setAttribute(attribute, name);
				element.setAttribute("content", content);
				document.head.appendChild(element);
			}
		};

		// Update standard meta tags
		updateMetaTag("description", fullDescription);
		updateMetaTag("keywords", fullKeywords);

		// Update Open Graph meta tags
		updateMetaTag("og:title", fullTitle, true);
		updateMetaTag("og:description", fullDescription, true);
		updateMetaTag("og:image", image, true);
		updateMetaTag("og:url", fullUrl, true);
		updateMetaTag("og:type", type, true);
		updateMetaTag("og:site_name", siteName, true);

		// Update Twitter Card meta tags
		updateMetaTag("twitter:title", fullTitle);
		updateMetaTag("twitter:description", fullDescription);
		updateMetaTag("twitter:image", image);
		updateMetaTag("twitter:url", fullUrl);

		// Update canonical URL
		let canonical = document.querySelector('link[rel="canonical"]');
		if (canonical) {
			canonical.setAttribute("href", fullUrl);
		} else {
			canonical = document.createElement("link");
			canonical.setAttribute("rel", "canonical");
			canonical.setAttribute("href", fullUrl);
			document.head.appendChild(canonical);
		}

		// Cleanup function to reset to defaults when component unmounts
		return () => {
			document.title = defaultTitle;
		};
	}, [fullTitle, fullDescription, fullKeywords, fullUrl, image, type]);

	return null; // This component doesn't render anything
};

export default SEO;
